import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Scales, ArrowClockwise, Copy, ArrowSquareOut } from '@phosphor-icons/react';
import { drawingRevApi } from './drawingRevApi';

const pct = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 100)}%`);

/** 超類似図面検索 — 図番が違っても形が同じ図面を出す。 */
export function SimilarDrawings({ member, labels, onCompare, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setData(null); setErr(null);
    try { setData(await drawingRevApi.similar(member.document_id)); }
    catch (e) { setErr(e?.response?.data?.detail || '類似図面を探せませんでした'); }
  }, [member.document_id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="border-t border-zinc-200 bg-zinc-50" data-testid="similar-panel">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-200">
        <Copy size={14} weight="duotone" className="text-teal-700" />
        <span className="text-[11px] font-semibold text-zinc-800">
          「{member.drawing_no || member.title}」に似た図面
        </span>
        <span className="text-[10px] text-zinc-500">
          形（線の分布）と表題欄の値で探しています。同じ図番の版は版管理側に出るため除いています。
        </span>
        <button type="button" onClick={onClose} data-testid="similar-close"
          className="ml-auto text-[11px] text-zinc-500 hover:text-zinc-900">閉じる</button>
      </div>

      {!data && !err && (
        <div className="py-8 text-center text-zinc-400" data-testid="similar-loading">
          <ArrowClockwise size={20} className="mx-auto mb-1.5 animate-spin" />
          <p className="text-[11px]">似ている図面を探しています…</p>
        </div>
      )}
      {err && <p className="px-3 py-4 text-[11px] text-amber-800" data-testid="similar-error">{err}</p>}

      {data?.reason === 'not_analysed' && (
        <p className="px-3 py-4 text-[11px] text-amber-800" data-testid="similar-not-analysed">{data.message}</p>
      )}

      {data && !data.reason && data.items.length === 0 && (
        <p className="px-3 py-5 text-[11px] text-zinc-500" data-testid="similar-empty">
          似ている図面は見つかりませんでした。
        </p>
      )}

      {data?.items?.map((it) => (
        <div key={it.document_id} data-testid={`similar-row-${it.document_id}`}
          className="flex items-center gap-3 px-3 py-2 border-b border-zinc-200 bg-white hover:bg-zinc-50 transition-colors">
          <span className="w-14 text-right">
            <span className="font-mono text-[12px] font-bold text-teal-700">{pct(it.score)}</span>
          </span>
          <span className="font-mono text-[11px] font-semibold text-zinc-900 w-36 truncate">
            {it.drawing_no || '図番なし'}
          </span>
          <span className="text-[11px] text-zinc-600 w-52 truncate" title={it.title}>{it.title}</span>
          <span className="text-[10px] text-zinc-500 tabular-nums w-28">
            形 {pct(it.image_similarity)}{it.hamming !== null ? `（差${it.hamming}/64）` : ''}
          </span>
          <span className="flex items-center gap-1 flex-1 min-w-0">
            {it.matched_fields.map((f) => (
              <span key={f} className="px-1.5 py-px rounded bg-teal-50 border border-teal-200 text-teal-800 text-[9px]">
                {labels[f] || f} 一致
              </span>
            ))}
            {it.matched_fields.length === 0 && (
              <span className="text-[10px] text-zinc-400">一致した項目なし（形だけで判定）</span>
            )}
          </span>
          <button type="button" onClick={() => onCompare(it)} data-testid={`similar-compare-${it.document_id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-300 text-[10px] text-zinc-700 hover:border-teal-500 hover:text-teal-700">
            <Scales size={11} />差分を見る
          </button>
          <a href={`/documents/all?open=${it.document_id}`} target="_blank" rel="noreferrer"
            data-testid={`similar-open-${it.document_id}`}
            className="text-zinc-400 hover:text-teal-700"><ArrowSquareOut size={13} /></a>
        </div>
      ))}
    </div>
  );
}

export default SimilarDrawings;
